import React, {
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  useForm,
  Controller,
  UseFormReturn,
  FieldValues,
} from "react-hook-form";
import { states } from "../../../../common/select/constants";
import { Input, Select, Button } from "@tyrannosaurustech/ui";
import styled from "styled-components";
import { Theme } from "../../../../components/atoms/theme";
import { ZIP_CODE_PATTERN } from "../../../../utils/regex";
import { CustomCheckbox } from "../../../../components/atoms/Checkbox";
import { useUser } from "../../../../hooks/useLogin";

const formatPhoneNumber = (value: string) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, "");
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

const FormContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  width: 100%;
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const DividerLine = styled.div`
  border-left: 2px solid ${Theme.colors.neutrals_400};
  margin: 0 1.5rem;
`;

const FormCardWrapper = styled.div`
  background: #fff;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: ${Theme.shadows.dropshadow_m};
  box-sizing: border-box;
  margin-bottom: 1.5rem;
`;

const PermissionsSectionHeader = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${Theme.colors.primaries_000};
  margin: 0 0 1rem 0;
`;

export interface LawFirmFormProps {
  initialData?: any;
  onFormSubmit: (data: any) => void;
  onFormCancel: (isDirty: boolean, reset: (values?: any) => void) => void;
  submitButtonText?: string;
  isProcessing: boolean;
  disableCancelButton?: boolean;
  renderButtonsExternal?: boolean;
  onFormStateChange?: (isDirty: boolean, isValid: boolean) => void;
}

export interface LawFirmFormRef {
  handleSubmit: UseFormReturn["handleSubmit"];
}

const LawFirmForm = forwardRef<LawFirmFormRef, LawFirmFormProps>(
  (
    {
      initialData,
      onFormSubmit,
      onFormCancel,
      submitButtonText,
      isProcessing,
      disableCancelButton = true,
      renderButtonsExternal = false,
      onFormStateChange,
    },
    ref
  ) => {
    const {
      control,
      register,
      handleSubmit,
      formState,
      reset,
      watch,
      setValue,
    } = useForm<FieldValues>({
      mode: "onChange",
      defaultValues: {
        name: "",
        address: "",
        state: "",
        city: "",
        zip: "",
        contactName: "",
        contactEmail: "",
        phone: "",
        canCreateRequests: true,
        requireCaseMatterId: false,
      },
    });

    useImperativeHandle(ref, () => ({
      handleSubmit,
    }));

    const { isDirty, isValid } = formState;
    useEffect(() => {
      if (onFormStateChange) {
        onFormStateChange(isDirty, isValid);
      }
    }, [isDirty, isValid, onFormStateChange]);

    const transformedStates = useMemo(() => {
      return [
        { id: "", label: "Select State" },
        ...states.map((s) => ({ id: s.value, label: s.label })),
      ];
    }, []);

    const user = useUser();

    useEffect(() => {
      if (initialData) {
        reset({
          name: initialData.name || "",
          address: initialData.address || "",
          state: initialData.state || "", // Changed from undefined to empty string
          city: initialData.city || "",
          zip: initialData.zip || "",
          contactName: initialData.contactName || "",
          contactEmail: initialData.contactEmail || "",
          phone: initialData.phone || "",
          canCreateRequests:
            initialData.canCreateRequests !== undefined
              ? initialData.canCreateRequests
              : true,
          requireCaseMatterId: initialData.requireCaseMatterId || false,
        });
      }
    }, [initialData, reset]);

    const onSubmit = handleSubmit(async (data) => {
      onFormSubmit(data);
    });

    const watchedPhone = watch("phone");

    useEffect(() => {
      const formattedValue = formatPhoneNumber(watchedPhone);
      if (formattedValue !== watchedPhone) {
        setValue("phone", formattedValue, {
          shouldValidate: true,
        });
      }
    }, [watchedPhone, setValue]);

    return (
      <>
        <FormCardWrapper>
          {!renderButtonsExternal && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "1.5rem",
              }}
            >
              <Button
                type="button"
                base="secondary"
                onClick={() => onFormCancel(formState.isDirty, reset)}
                label="Cancel"
                disabled={disableCancelButton && !formState.isDirty}
              />
              <div style={{ marginLeft: "1rem" }} />
              <Button
                type="button"
                onClick={onSubmit}
                label={submitButtonText}
                loading={isProcessing}
                disabled={!formState.isDirty || !formState.isValid}
              />
            </div>
          )}
          <form onSubmit={onSubmit}>
            <FormContainer>
              <FormColumn>
                <Input
                  label="Firm Name"
                  placeholder="Firm Name"
                  required
                  className="w-full"
                  {...register("name", { required: true })}
                />
                <div style={{ display: "flex", gap: "1.5rem" }}>
                  <Input
                    label="Address"
                    placeholder="Address"
                    required
                    className="w-full flex-1"
                    {...register("address", { required: true })}
                  />
                  <Input
                    label="City"
                    placeholder="City"
                    required
                    className="w-full flex-1"
                    {...register("city", { required: true })}
                  />
                </div>
                <div style={{ display: "flex", gap: "1.5rem" }}>
                  <div className="w-full flex-1">
                    <Controller
                      name="state"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange, value } }) => (
                        <Select
                          label="State"
                          value={
                            transformedStates.find(
                              (option) => option.id === value
                            ) || transformedStates[0]
                          }
                          onChange={(selectedOption) =>
                            onChange(
                              selectedOption?.id === ""
                                ? ""
                                : selectedOption?.id
                            )
                          }
                          options={transformedStates}
                          className="w-full"
                          required
                        />
                      )}
                    />
                  </div>
                  <div className="w-full flex-1">
                    <Input
                      label="Zip Code"
                      placeholder="Zip Code"
                      required
                      className="w-full"
                      {...register("zip", {
                        required: true,
                        pattern: {
                          value: ZIP_CODE_PATTERN,
                          message: "Enter a valid zip code",
                        },
                      })}
                    />
                  </div>
                </div>
              </FormColumn>
              <DividerLine />
              <FormColumn>
                <Input
                  label="Contact Name"
                  placeholder="Contact Name"
                  required
                  className="w-full"
                  {...register("contactName", { required: true })}
                />
                <Input
                  label="Email"
                  placeholder="Email"
                  required
                  className="w-full"
                  {...register("contactEmail", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                />
                <Input
                  label="Phone Number"
                  placeholder="Phone Number"
                  className="w-full"
                  {...register("phone", {
                    required: false,
                    pattern: {
                      value: /^\(\d{3}\) \d{3}-\d{4}$/,
                      message:
                        "Invalid phone number format (e.g., (123) 456-7890)",
                    },
                  })}
                />
                {(user?.isAdmin() || user?.isClientManager()) && (
                  <div style={{ marginTop: "0.5rem" }}>
                    <Controller
                      name="canCreateRequests"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CustomCheckbox
                          checked={value !== undefined ? value : true}
                          onChange={onChange}
                          label="Allow Request Creation"
                        />
                      )}
                    />
                  </div>
                )}
              </FormColumn>
            </FormContainer>
          </form>
          {!renderButtonsExternal && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
              }}
            >
              <Button
                type="button"
                base="secondary"
                onClick={() => onFormCancel(formState.isDirty, reset)}
                label="Cancel"
                disabled={disableCancelButton && !formState.isDirty}
              />
              <div style={{ marginLeft: "1rem" }} />
              <Button
                type="button"
                onClick={onSubmit}
                label={submitButtonText}
                loading={isProcessing}
                disabled={!formState.isDirty || !formState.isValid}
              />
            </div>
          )}
        </FormCardWrapper>
        {(user?.isAdmin() || user?.isClientManager()) && (
          <FormCardWrapper>
            <PermissionsSectionHeader>
              For New Patients
            </PermissionsSectionHeader>
            <Controller
              name="requireCaseMatterId"
              control={control}
              render={({ field: { onChange, value } }) => (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <span
                    style={{
                      fontWeight: value ? 600 : 400,
                      color: value
                        ? Theme.colors.primaries_000
                        : Theme.colors.neutrals_200,
                      fontSize: "0.875rem",
                    }}
                  >
                    CASE MATTER ID REQUIRED
                  </span>
                  <label
                    style={{
                      position: "relative",
                      display: "inline-block",
                      width: "2.75rem",
                      height: "1.5rem",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={value || false}
                      onChange={(e) => onChange(e.target.checked)}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: value
                          ? Theme.colors.primaries_000
                          : "#ccc",
                        borderRadius: "1.5rem",
                        transition: "background-color 0.2s",
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          content: '""',
                          height: "1.125rem",
                          width: "1.125rem",
                          left: value ? "calc(100% - 1.3125rem)" : "0.1875rem",
                          bottom: "0.1875rem",
                          backgroundColor: "white",
                          borderRadius: "50%",
                          transition: "left 0.2s",
                        }}
                      />
                    </span>
                  </label>
                  <span
                    style={{
                      fontWeight: !value ? 600 : 400,
                      color: !value
                        ? Theme.colors.primaries_000
                        : Theme.colors.neutrals_200,
                      fontSize: "0.875rem",
                    }}
                  >
                    CASE MATTER ID NOT REQUIRED
                  </span>
                </div>
              )}
            />
          </FormCardWrapper>
        )}
      </>
    );
  }
);

export default LawFirmForm;
